// Shared by browser saves and the server's compact write journal.
const unsafe = new Set(['__proto__', 'prototype', 'constructor'])
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value)
const same = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b)

export function createPatch(before, after) {
  const changes = []
  function visit(a, b, path) {
    if (same(a, b)) return
    if (path.length >= 64) {
      changes.push({ op: 'set', path, value: structuredClone(b) })
      return
    }
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length === b.length) {
        a.forEach((value, i) => visit(value, b[i], [...path, i]))
      } else {
        let first = 0, tail = 0
        while (first < Math.min(a.length, b.length) && same(a[first], b[first])) first++
        while (tail < Math.min(a.length, b.length) - first && same(a[a.length - 1 - tail], b[b.length - 1 - tail])) tail++
        changes.push({ op: 'splice', path, index: first, deleteCount: a.length - first - tail, values: structuredClone(b.slice(first, b.length - tail)) })
      }
    } else if (object(a) && object(b)) {
      // Preserve arbitrary imported JSON without ever traversing prototype keys.
      if ([...Object.keys(a), ...Object.keys(b)].some(key => unsafe.has(key))) {
        changes.push({ op: 'set', path, value: structuredClone(b) })
        return
      }
      // Match JSON serialization: optional object fields set to undefined are
      // absent in a whole-project save too.
      for (const key of Object.keys(a)) if (a[key] !== undefined && b[key] === undefined) changes.push({ op: 'remove', path: [...path, key] })
      for (const key of Object.keys(b)) if (b[key] !== undefined) visit(a[key], b[key], [...path, key])
    } else if (typeof a === 'string' && typeof b === 'string') {
      let first = 0, tail = 0
      while (first < Math.min(a.length, b.length) && a[first] === b[first]) first++
      while (tail < Math.min(a.length, b.length) - first && a[a.length - 1 - tail] === b[b.length - 1 - tail]) tail++
      const edit = { op: 'text', path, index: first, deleteCount: a.length - first - tail, text: b.slice(first, b.length - tail) }
      const replace = { op: 'set', path, value: b }
      changes.push(JSON.stringify(edit).length < JSON.stringify(replace).length ? edit : replace)
    } else {
      changes.push({ op: 'set', path, value: structuredClone(b) })
    }
  }
  visit(before, after, [])
  return changes
}

export function applyPatch(value, changes, maxOperations = 10000) {
  if (!Array.isArray(changes) || changes.length > maxOperations) throw new Error('Invalid number of edit operations.')
  let result = structuredClone(value)
  function keyFor(container, key, existing = true) {
    if (Array.isArray(container)) {
      if (!Number.isSafeInteger(key) || key < 0 || key >= container.length) throw new Error('Invalid array edit path.')
    } else if (!object(container) || typeof key !== 'string' || unsafe.has(key) || (existing && !Object.hasOwn(container, key))) {
      throw new Error('Invalid edit path.')
    }
    return key
  }
  for (const change of changes) {
    if (!change || !['set', 'remove', 'splice', 'text'].includes(change.op) || !Array.isArray(change.path) || change.path.length > 64 ||
      change.path.some(key => (typeof key !== 'string' && !Number.isSafeInteger(key)) || unsafe.has(key))) throw new Error('Invalid edit operation.')
    let target = result
    const range = change.op === 'splice' || change.op === 'text'
    for (const key of change.path.slice(0, range ? undefined : -1)) target = target[keyFor(target, key)]
    if (range) {
      if ((change.op === 'splice' ? !Array.isArray(target) || !Array.isArray(change.values) : typeof target !== 'string' || typeof change.text !== 'string') ||
        !Number.isSafeInteger(change.index) || change.index < 0 || change.index > target.length ||
        !Number.isSafeInteger(change.deleteCount) || change.deleteCount < 0 || change.deleteCount > target.length - change.index) throw new Error('Invalid range edit.')
      // Avoid spreading untrusted, potentially large arrays onto the call stack.
      const next = change.op === 'text' ? target.slice(0, change.index) + change.text + target.slice(change.index + change.deleteCount) :
        [...target.slice(0, change.index), ...structuredClone(change.values), ...target.slice(change.index + change.deleteCount)]
      if (!change.path.length) result = next
      else {
        let parent = result
        for (const key of change.path.slice(0, -1)) parent = parent[keyFor(parent, key)]
        parent[keyFor(parent, change.path.at(-1))] = next
      }
    } else if (!change.path.length) {
      if (change.op !== 'set' || !Object.hasOwn(change, 'value')) throw new Error('Invalid root edit.')
      result = structuredClone(change.value)
    } else {
      const key = keyFor(target, change.path.at(-1), change.op !== 'set')
      if (change.op === 'remove') {
        if (Array.isArray(target)) throw new Error('Use an array edit to remove an item.')
        delete target[key]
      } else {
        if (!Object.hasOwn(change, 'value')) throw new Error('An edit value is missing.')
        target[key] = structuredClone(change.value)
      }
    }
  }
  return result
}
